import { useState } from "react";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";
import { useSweep } from "@/components/primitives";

export const InquiryForm = ({ type, division, cta = "Submit", note, requireAck = false, testid = "inquiry-form" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const sweep = useSweep();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error("Please enter a valid email.");
    if (requireAck && !ack) return toast.error("Please confirm the qualification acknowledgment.");
    setLoading(true);
    try {
      await submitInquiry({ type, division, acknowledged: ack, ...form });
      setDone(true);
      toast.success("Your submission has been received. All inquiries are reviewed confidentially.");
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[rgba(180,139,63,0.28)] px-4 py-3 text-[var(--bone)] font-serif2 text-lg placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors";

  if (done)
    return (
      <div className="corner-frame mx-auto px-8 py-12 text-center jmh-reading-shell" data-testid={`${testid}-success`}>
        <p className="font-display gold-text uppercase tracking-wide2 text-lg">Received</p>
        <p className="mt-4 text-[var(--muted)]">Inquiries are reviewed confidentially to determine whether the circumstances fall within the organization’s scope. A response is provided at our discretion.</p>
      </div>
    );

  return (
    <form onSubmit={onSubmit} className="mx-auto flex flex-col gap-5 jmh-reading-shell" data-testid={testid}>
      <input className={inputCls} placeholder="Full Name" value={form.name} onChange={set("name")} data-testid={`${testid}-name`} />
      <input className={inputCls} placeholder="Email" type="email" value={form.email} onChange={set("email")} data-testid={`${testid}-email`} />
      <input className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid={`${testid}-phone`} />
      <textarea className={inputCls} rows={4} placeholder="Briefly describe your matter (optional)" value={form.message} onChange={set("message")} data-testid={`${testid}-message`} />
      {requireAck && (
        <label className="flex items-start gap-3 text-sm text-[var(--muted)] cursor-pointer" data-testid={`${testid}-ack`}>
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1 accent-[var(--gold)]" />
          <span>I acknowledge that engagement is selective, not guaranteed, and that the information I provide is accurate and complete.</span>
        </label>
      )}
      <button disabled={loading} className="jmh-cta mt-2 disabled:opacity-50" data-testid={`${testid}-submit`}>
        {loading ? "Submitting…" : cta}
      </button>
      {note && <p className="text-center text-xs text-[var(--muted)] mt-2">{note}</p>}
    </form>
  );
};
